import { getAttributeLeafName } from './helpers';

/**
 * Normalize diagnostic_map API envelope (same as vertical incidents).
 * Accepts { code, message, response } or a direct payload.
 * Returns the API payload as-is (no client-side defaults).
 */
export function extractDiagnosticMapData(response) {
    if (!response || typeof response !== 'object') return null;

    if (Object.prototype.hasOwnProperty.call(response, 'response')) {
        const code = Number(response.code);
        if (!Number.isNaN(code) && code !== 200) return null;
        const payload = response.response;
        if (!payload || typeof payload !== 'object') return null;
        return payload;
    }

    if (response.datasource || response.kpi || response.pipelines || response.stats || response.links) {
        return response;
    }

    return null;
}

/** True when API payload has something to render in the diagnostics modal. */
export function hasDiagnosticMapContent(data) {
    if (!data || typeof data !== 'object') return false;
    if (data.kpi && (data.kpi.kpi_id != null || data.kpi.kpi_name)) return true;
    if (data.datasource && (data.datasource.datasource_id != null || data.datasource.datasource_name)) return true;
    if (Array.isArray(data.pipelines) && data.pipelines.length > 0) return true;
    if (Array.isArray(data.links) && data.links.length > 0) return true;
    const stats = data.stats || {};
    return ['kpi_count', 'datasource_count', 'pipeline_count', 'dataset_count', 'attribute_count']
        .some((key) => Number(stats[key]) > 0);
}

function slugify(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

function normalizePipelineDataset(dataset = {}) {
    return {
        data_set_id: dataset.data_set_id ?? dataset.dataSetId ?? dataset.id ?? null,
        data_set_name: dataset.data_set_name || dataset.dataSetName || dataset.dataset || dataset.name || '—',
        status: dataset.status || 'unaffected'
    };
}

function normalizePipelineEntry(pipeline = {}, fallbackDataset = null) {
    const pipelineId = pipeline.pipeline_id ?? pipeline.pipelineId ?? pipeline.id ?? null;
    const pipelineName = pipeline.pipeline_name || pipeline.pipelineName || pipeline.name || '';
    const datasets = Array.isArray(pipeline.datasets)
        ? pipeline.datasets.map(normalizePipelineDataset)
        : [];

    if (fallbackDataset && !datasets.some((ds) => ds.data_set_name === fallbackDataset.data_set_name)) {
        datasets.push(normalizePipelineDataset(fallbackDataset));
    }

    return {
        pipeline_id: pipelineId ?? slugify(pipelineName || 'pipeline'),
        pipeline_name: pipelineName || 'Pipeline',
        status: pipeline.status || 'unaffected',
        datasets,
        metrics: Array.isArray(pipeline.metrics) ? pipeline.metrics : []
    };
}

function derivePipelineName(asset = {}, datasourceName = '') {
    const explicit = asset.pipeline_name || asset.pipelineName;
    if (explicit) return explicit;

    const datasetSlug = slugify(asset.dataset);
    if (datasetSlug) return `${datasetSlug}_pipeline`;

    const datasourceSlug = slugify(datasourceName);
    if (datasourceSlug) return `${datasourceSlug}_ingest_pipeline`;

    return 'data_ingest_pipeline';
}

function derivePipelineId(asset = {}, pipelineName = '') {
    return asset.pipeline_id
        ?? asset.pipelineId
        ?? slugify(pipelineName);
}

function buildPipelinesFromAssets(assets = [], datasourceName = '', row = {}) {
    if (Array.isArray(row.pipelines) && row.pipelines.length) {
        return row.pipelines.map((pipeline) => normalizePipelineEntry(pipeline));
    }

    const pipelineMap = new Map();

    const addPipelineForAsset = (asset) => {
        if (!asset) return;

        if (Array.isArray(asset.pipelines) && asset.pipelines.length) {
            asset.pipelines.forEach((pipeline) => {
                const normalized = normalizePipelineEntry(pipeline, {
                    data_set_id: asset.id,
                    data_set_name: asset.dataset,
                    status: 'unaffected'
                });
                pipelineMap.set(String(normalized.pipeline_id), normalized);
            });
            return;
        }

        const dsName = asset.dataset || 'Dataset';
        const pipelineName = derivePipelineName(asset, datasourceName);
        const pipelineId = derivePipelineId(asset, pipelineName);
        const key = String(pipelineId);

        if (!pipelineMap.has(key)) {
            pipelineMap.set(key, {
                pipeline_id: pipelineId,
                pipeline_name: pipelineName,
                status: asset.pipeline_status || asset.status || 'unaffected',
                datasets: [],
                metrics: Array.isArray(asset.metrics) ? asset.metrics : []
            });
        }

        const pipeline = pipelineMap.get(key);
        if (!pipeline.datasets.some((ds) => ds.data_set_name === dsName)) {
            pipeline.datasets.push({
                data_set_id: asset.id,
                data_set_name: dsName,
                status: 'unaffected'
            });
        }
    };

    assets.forEach(addPipelineForAsset);

    if (!pipelineMap.size && datasourceName && datasourceName !== '—') {
        const pipelineName = `${slugify(datasourceName)}_ingest_pipeline`;
        pipelineMap.set(pipelineName, {
            pipeline_id: slugify(pipelineName),
            pipeline_name: pipelineName,
            status: 'unaffected',
            datasets: assets
                .filter((asset) => asset?.dataset)
                .map((asset) => ({
                    data_set_id: asset.id,
                    data_set_name: asset.dataset,
                    status: 'unaffected'
                })),
            metrics: []
        });
    }

    return Array.from(pipelineMap.values());
}

function buildDiagnosticLinks(datasource = {}, pipelines = [], kpi = {}) {
    const links = [];
    const datasourceName = datasource.datasource_name;
    if (!datasourceName || datasourceName === '—' || !pipelines.length) {
        return links;
    }

    const sourceKey = `${datasourceName}:${datasource.datasource_id ?? ''}`.replace(/:$/, '');

    pipelines.forEach((pipeline, index) => {
        const destinationKey = `${pipeline.pipeline_id}:${pipeline.pipeline_name}`;
        links.push({
            source: index === 0 ? sourceKey : `${pipelines[0].pipeline_id}:${pipelines[0].pipeline_name}`,
            destination: destinationKey,
            status: pipeline.status || 'unaffected'
        });
    });

    const lastPipeline = pipelines[pipelines.length - 1];
    if (lastPipeline && kpi?.kpi_name) {
        links.push({
            source: `${lastPipeline.pipeline_id}:${lastPipeline.pipeline_name}`,
            destination: `${kpi.kpi_name}:${kpi.kpi_id ?? ''}`.replace(/:$/, ''),
            status: 'unaffected'
        });
    }

    return links;
}

/**
 * Build LineageModal / diagnostic_map JSON from a Data Assets Mapping leaf row.
 */
export function buildMappingDiagnosticMapFromRow(row = {}, indexes = null) {
    const assets = Array.isArray(row.mappedAssets) && row.mappedAssets.length
        ? row.mappedAssets
        : [];

    let primaryAsset = assets[0] || null;
    if (!primaryAsset && row.assetId != null && indexes?.getAsset) {
        primaryAsset = indexes.getAsset(row.assetId) || null;
    }

    const datasourceName = primaryAsset?.datasource
        || row.datasource
        || row.datasourceName
        || (Array.isArray(row.datasources) ? row.datasources[0] : '')
        || '—';
    const datasetName = primaryAsset?.dataset || row.dataset || row.assetName || '—';
    const attributeName = primaryAsset?.attribute || row.attribute || row.attributeName || '—';

    const mappedAttributes = assets
        .map((asset) => {
            const name = asset.attribute ? getAttributeLeafName(asset.attribute) : '';
            if (!name) return null;
            return { name, status: 'unaffected' };
        })
        .filter(Boolean);

    if (!mappedAttributes.length && attributeName && attributeName !== '—') {
        mappedAttributes.push({
            name: getAttributeLeafName(attributeName),
            status: 'unaffected'
        });
    }

    const datasets = [];
    const seenDatasets = new Set();
    assets.forEach((asset) => {
        const dsName = asset.dataset || 'Dataset';
        const key = String(asset.id ?? dsName);
        if (seenDatasets.has(key)) return;
        seenDatasets.add(key);
        datasets.push({
            data_set_id: asset.id,
            data_set_name: dsName,
            status: 'unaffected'
        });
    });

    if (!datasets.length && datasetName && datasetName !== '—') {
        datasets.push({
            data_set_id: row.assetId,
            data_set_name: datasetName,
            status: 'unaffected'
        });
    }

    const pipelines = buildPipelinesFromAssets(
        primaryAsset && !assets.length ? [primaryAsset] : assets,
        datasourceName,
        row
    );

    const kpiPayload = {
        kpi_id: row.kpiId,
        kpi_name: row.kpiName || 'KPI',
        formula: row.formula || '—',
        mapped_attributes: mappedAttributes.length
            ? mappedAttributes
            : (attributeName !== '—'
                ? { name: getAttributeLeafName(attributeName), status: 'unaffected' }
                : [])
    };

    const datasourcePayload = {
        datasource_id: primaryAsset?.id,
        datasource_name: datasourceName,
        type: primaryAsset?.datasource_type || primaryAsset?.type || 'Source',
        datasets
    };

    const attributeCount = mappedAttributes.length || (attributeName !== '—' ? 1 : 0);
    const links = buildDiagnosticLinks(datasourcePayload, pipelines, kpiPayload);

    return {
        stats: {
            kpi_count: 1,
            datasource_count: datasourceName !== '—' ? 1 : 0,
            pipeline_count: pipelines.length,
            dataset_count: datasets.length,
            attribute_count: attributeCount
        },
        kpi: kpiPayload,
        datasource: datasourcePayload,
        pipelines,
        links,
        last_sync_time: new Date().toISOString()
    };
}
